import React, { useState, useEffect, useMemo } from "react";
import SharedLayout from "./SharedLayout";
import { useNavigate, useParams } from "react-router-dom";
import { FiChevronLeft } from "react-icons/fi";
import apiClient from "./services/apiClient";
import format from "date-fns/format";
import InfoModal from "./modals/InfoModals"; // ✅ uyarılar InfoModal ile
import { tr } from "date-fns/locale"; // Import Turkish locale

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL; // Removed

const safeFormatDate = (ts) => {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return "-";
    return format(d, "dd MMMM yyyy 'saat' HH:mm", { locale: tr });
  } catch {
    return "-";
  }
};

const LearnerDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [learner, setLearner] = useState({
    id: "",
    name: "",
    surname: "",
    number: "",
    email: "",
    active: true,
    addDate: "",
    completedCount: 30,
    totalCount: 32,
    groups: [],
    courses: [],
    enrollments: [],
    lessonProgress: [],
    courseProgress: [],
  });

  // InfoModal state
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const openInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  const completionPct = useMemo(() => {
    const total = Number(learner.totalCount) || 0;
    const done = Number(learner.completedCount) || 0;
    if (total > 0) return Math.round((done / total) * 100);
    return 0;
  }, [learner.completedCount, learner.totalCount]);

  const dashOffset = 100 - Math.max(0, Math.min(completionPct, 100));

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        // apiClient unwrap - use /learners/:id
        const data = await apiClient.get(`/learners/${id}`);

        console.log(data);

        const {
          groups = [],
          courseProgress = [],
          lessonProgress = [],
          enrollments = [],
          analytics = {},
          learnerData = {},
        } = data || {};

        setLearner((prev) => ({
          ...prev,
          name: learnerData.name || "",
          surname: learnerData.surname || "",
          number: learnerData.number || "",
          email: learnerData.email || "",
          active: !!learnerData.active,
          addDate: safeFormatDate(learnerData.createdAt),
          completedCount: Number(analytics.totalCompletedCourses) || 0,
          totalCount: Number(analytics.totalEnrolledCourses) || 0,
          groups: groups.map((g) => g.name),
          courses: enrollments
            .map((e) => {
              const courseName = e.course?.name || "";
              const courseStatus = e.course?.status;
              return courseStatus === "ARCHIVED"
                ? `${courseName} (ARŞİVLENMİŞ)`
                : courseName;
            })
            .filter(Boolean),
          enrollments,
          lessonProgress,
          courseProgress,
        }));
      } catch (error) {
        console.error("Error fetching learner:", error);
        openInfo(
          "error",
          "Yükleme Hatası",
          "Kullanıcı verileri alınamadı. Lütfen tekrar deneyin.",
        );
      }
    })();
  }, [id]);

  const handleCourseClick = (name) => {
    const selectedEnrollment = learner.enrollments.find(
      (e) => e.course?.name === name,
    );
    const selectedCourseProgress = learner.courseProgress.find(
      (c) => c.course?.name === name,
    );

    if (!selectedEnrollment) {
      openInfo("warning", "Kurs Bulunamadı", "Bu kursa ait detay bulunamadı.");
      return;
    }

    const slug = String(name).toLowerCase().replace(/\s+/g, "-");
    navigate(`/lesson-analyze/${slug}`, {
      state: {
        course: selectedEnrollment,
        learner,
        lessonProgress: learner.lessonProgress,
        courseProgress: selectedCourseProgress || null,
      },
    });
  };

  return (
    <SharedLayout pageTitle="KATILIMCI DETAY BİLGİLERİ">
      <div className="rounded-[24px] bg-gray-50 ring-1 ring-emerald-100 p-5 sm:p-6 lg:p-8 shadow-sm">
        <div className="max-w-8xl mx-auto px-1 sm:px-2">
          {/* Üst şerit */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-white px-5 py-2 rounded-full shadow-sm">
                <span className="font-medium text-base sm:text-lg">
                  {learner.name} {learner.surname}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 self-start sm:self-auto">
              <div className="text-lg sm:text-xl font-bold tracking-wide">
                DURUM:
              </div>
              <span
                className={`px-3 py-1 rounded-full text-lg font-medium ${
                  learner.active ? "text-green-600" : "text-gray-600"
                }`}
              >
                {learner.active ? "Aktif" : "Pasif"}
              </span>

              <button
                onClick={() => navigate(-1)}
                className="ml-2 inline-flex items-center justify-center h-9 w-9 rounded-full bg-green-50 text-green-600 hover:bg-green-100"
                title="Geri"
              >
                <FiChevronLeft size={42} />
              </button>
            </div>
          </div>

          {/* Üst grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-10 items-end">
            {/* İletişim Detayları */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-3">
                İLETİŞİM DETAYLARI
              </h3>
              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-5 sm:p-6 min-h-[220px]">
                <dl className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <dt className="uppercase tracking-wide text-xs text-gray-500">
                      Eklenme Tarihi:
                    </dt>
                    <dd className="text-gray-800 mt-1 sm:mt-0">
                      {learner.addDate || "-"}
                    </dd>
                  </div>

                  <div className="border-t border-gray-100 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <dt className="uppercase tracking-wide text-xs text-gray-500">
                      Telefon Numarası:
                    </dt>
                    <dd className="text-gray-800 mt-1 sm:mt-0 break-all">
                      {learner.number || "-"}
                    </dd>
                  </div>

                  <div className="border-t border-gray-100 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <dt className="uppercase tracking-wide text-xs text-gray-500">
                      E-mail Adresi:
                    </dt>
                    <dd className="text-gray-800 mt-1 sm:mt-0 break-all">
                      {learner.email || "-"}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Course Completion Rate */}
            <div
              className="bg-white rounded-xl shadow-sm p-6 overflow-hidden
                         w-full max-w-[460px] min-h-[220px]
                         justify-self-center md:justify-self-center"
            >
              <h3 className="text-lg font-semibold tracking-wide text-gray-700">
                KURS TAMAMLANMA ORANI
              </h3>
              <p className="text-[11px] text-gray-500 mt-1 uppercase">
                Başarılı çalışma!
              </p>

              <div className="mt-4 flex justify-center">
                <svg
                  viewBox="0 0 36 36"
                  width="92"
                  height="92"
                  aria-label="completion"
                >
                  <circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke="#e6e6e6"
                    strokeWidth="3"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke="#07183c"
                    strokeWidth="3"
                    strokeDasharray="100"
                    strokeDashoffset={dashOffset}
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)"
                  />
                  <text
                    x="18"
                    y="20.5"
                    textAnchor="middle"
                    fontSize="9"
                    fill="#07183c"
                    fontWeight="bold"
                  >
                    %{completionPct}
                  </text>
                </svg>
              </div>

              <div className="mt-2 text-center text-xs text-gray-500">
                {learner.completedCount}/{learner.totalCount}
              </div>
            </div>
          </div>

          {/* Alt grid: Groups & Courses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 justify-items-center mt-4">
            {/* Groups */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden w-full max-w-[460px] min-h-[220px]">
              <div className="bg-green-500 px-5 py-3">
                <h4 className="text-white text-sm font-semibold">GRUPLAR</h4>
              </div>
              <ul className="p-5 sm:p-6 space-y-3">
                {(learner.groups || ["CPS", "CPS 2", "OG"]).map((g, i) => (
                  <li key={i} className="text-gray-800 text-lg">
                    {g}
                  </li>
                ))}
              </ul>
            </div>

            {/* Courses */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden w-full max-w-[460px] min-h-[220px]">
              <div className="bg-green-500 px-5 py-3">
                <h4 className="text-white text-sm font-semibold">KURSLAR</h4>
              </div>
              <ul className="p-5 sm:p-6 space-y-3">
                {(learner.courses || ["CPS", "CPS 2", "OG"]).map((c, i) => (
                  <li
                    key={i}
                    className="text-gray-800 text-lg font-semibold cursor-pointer hover:text-emerald-600"
                    onClick={() => handleCourseClick(c)}
                    title="Detaya git"
                  >
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Info Modal */}
      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={closeInfo}
      />
    </SharedLayout>
  );
};

export default LearnerDetails;
